// ComplianceLayer — the disclaimer machinery for Landlord Autopilot.
//
// Three surfaces:
//   1. <DisclaimerFooter/> — global slim footer on every admin page
//   2. <TosGate/>          — one-time acceptance modal after signup (persists to
//                            db.landlord_tos_attestations with timestamp + IP)
//   3. <ContextBanner/>    — colored "know this before you tap Send" strip on
//                            Lease Builder / AI Screening / Rent Portal
//
// All designed to be legally load-bearing but visually calm — a landlord
// shouldn't feel like they're using WestLaw.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useIndustry } from "@/context/IndustryContext";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import { ShieldCheck, Warning, X, FileText, Trash, ArrowLeft, CheckCircle, UploadSimple } from "@phosphor-icons/react";

const DISCLAIMER_TEXT = (
  "Field Radar Landlord Autopilot is a software tool — not a licensed real estate broker, " +
  "attorney, or property manager. AI suggestions and document templates are starting points; " +
  "verify with counsel licensed in your jurisdiction before executing anything."
);

export function DisclaimerFooter() {
  const ind = useIndustry();
  if (ind?.industry !== "landlord") return null;
  return (
    <div className="mt-8 border-t border-slate-200 bg-slate-50 -mx-4 md:-mx-6 px-4 md:px-6 py-3 text-[10px] leading-snug text-slate-500"
         data-testid="landlord-disclaimer-footer">
      <div className="max-w-5xl mx-auto flex items-start gap-2">
        <ShieldCheck size={12} className="text-slate-400 flex-shrink-0 mt-0.5"/>
        <div>
          <span className="font-black uppercase tracking-widest text-slate-600">Legal notice · </span>
          {DISCLAIMER_TEXT}
        </div>
      </div>
    </div>
  );
}

export function TosGate() {
  const ind = useIndustry();
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ind?.industry !== "landlord") return;
    // Demo tenants (anonymous visitors) shouldn't be blocked by a legal
    // acknowledgment modal — they haven't bought anything yet. Suppress.
    if (ind?.is_demo || ind?.plan === "demo") return;
    (async () => {
      try {
        const r = await api.get("/landlord-auto/tos-attest");
        if (!r.data?.agreed) setOpen(true);
      } catch { /* not authed yet or endpoint missing — safe to no-op */ }
    })();
  }, [ind?.industry, ind?.is_demo, ind?.plan]);

  const agree = async () => {
    setSaving(true);
    try {
      await api.post("/landlord-auto/tos-attest", { version: "v1" });
      setOpen(false);
    } catch { setSaving(false); }
  };

  if (ind?.industry !== "landlord" || !open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="landlord-tos-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="p-5 bg-slate-900 text-white flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center"><ShieldCheck weight="fill" size={18}/></div>
          <div>
            <div className="font-display font-black text-lg leading-tight">Before you get started</div>
            <div className="text-xs text-slate-400">One-tap acknowledgment · required to continue</div>
          </div>
        </div>
        <div className="p-5 space-y-3 text-sm text-slate-700">
          <p>{DISCLAIMER_TEXT}</p>
          <p>Specifically, you understand:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Lease templates are <b>starting drafts</b>. Have a PA-licensed attorney review before signing anything binding.</li>
            <li>AI screening is a <b>triage aid</b>, not a decision. Adverse actions must comply with the federal Fair Housing Act, Pennsylvania Human Relations Act, and (for Philly rentals) the Fair Practices Ordinance + Fair Chance Housing Ordinance.</li>
            <li>Rent payments flow through <b>Stripe</b> directly to your bank. Field Radar is not a payment processor or escrow agent.</li>
            <li>Philadelphia rentals require a current <b>L&I Rental License</b> and <b>Certificate of Rental Suitability</b> before a lease is enforceable. You are responsible for both.</li>
          </ul>
          <label className="flex items-start gap-2 pt-3 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
              data-testid="landlord-tos-checkbox"
              className="mt-1 w-5 h-5 accent-emerald-600"/>
            <span className="text-sm">I understand and agree. I'll use this as a tool, not as legal advice.</span>
          </label>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button onClick={agree} disabled={!checked || saving}
            data-testid="landlord-tos-agree"
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl disabled:opacity-40 shadow">
            {saving ? "Saving…" : "Agree & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

const BANNER_VARIANTS = {
  amber: "bg-amber-50 border-amber-400 text-amber-900",
  sky:   "bg-sky-50 border-sky-400 text-sky-900",
  fuchsia: "bg-fuchsia-50 border-fuchsia-400 text-fuchsia-900",
};
export function ContextBanner({ variant = "amber", icon: Icon = Warning, title, children, testid }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className={`p-3 rounded-lg border-l-4 ${BANNER_VARIANTS[variant]} mb-4 relative`} data-testid={testid}>
      <div className="flex items-start gap-2 pr-6">
        <Icon weight="fill" size={16} className="flex-shrink-0 mt-0.5"/>
        <div className="text-xs">
          {title && <div className="font-black uppercase tracking-widest text-[10px] mb-0.5">{title}</div>}
          <div>{children}</div>
        </div>
      </div>
      <button onClick={() => setDismissed(true)} className="absolute top-2 right-2 opacity-60 hover:opacity-100" aria-label="Dismiss">
        <X size={12}/>
      </button>
    </div>
  );
}

// ─── Legal Health chip (dashboard) ─────────────────────────────
const HEALTH_META = {
  green: { color: "bg-emerald-500 text-white", label: "GREEN" },
  yellow: { color: "bg-amber-500 text-white", label: "YELLOW" },
  red: { color: "bg-red-500 text-white", label: "RED" },
};
export function LegalHealthChip() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.get("/landlord-auto/legal-health").then(r => setHealth(r.data)).catch(() => setHealth(null));
  }, []);
  if (!health) return null;
  const meta = HEALTH_META[health.overall] || HEALTH_META.yellow;
  const rentalsNeedingLove = (health.rentals || []).filter(r => r.status !== "green");
  return (
    <Link to="/admin/landlord/legal-health" data-testid="legal-health-chip"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${meta.color} shadow font-black uppercase tracking-widest text-[10px]`}>
      <ShieldCheck weight="fill" size={12}/> Legal · {meta.label}
      {rentalsNeedingLove.length > 0 && (
        <span className="ml-1 text-[10px] opacity-90 normal-case font-bold">
          {rentalsNeedingLove.length} unit{rentalsNeedingLove.length === 1 ? "" : "s"} to complete
        </span>
      )}
    </Link>
  );
}

// ─── Legal Health overview page ────────────────────────────────
export function LegalHealthOverview() {
  const [health, setHealth] = useState(null);
  const load = async () => { try { const r = await api.get("/landlord-auto/legal-health"); setHealth(r.data); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);
  if (!health) return <AdminShell><div className="p-6 text-slate-500">Loading…</div></AdminShell>;
  const meta = HEALTH_META[health.overall];
  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="legal-health-page">
        <div className="flex items-center gap-3 mb-4">
          <div className={`h-11 w-11 rounded-xl ${meta.color} flex items-center justify-center`}><ShieldCheck weight="fill" size={22}/></div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Legal Health</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">Overall: {meta.label} · TOS {health.tos_ok ? "accepted" : "NOT accepted"}</div>
          </div>
        </div>
        <ContextBanner variant="sky" title="What this checks"
          testid="legal-health-what-checks">
          Every Philadelphia rental needs a current <b>L&I Rental License</b>, a
          {" "}<b>Certificate of Rental Suitability</b>, and (if built before 1978) a
          <b> Lead-Safe Certificate</b> attached to the lease packet. Upload real
          numbers per rental so the executed lease PDF cites them by number instead
          of just an attestation.
        </ContextBanner>
        <div className="space-y-3">
          {(health.rentals || []).map(r => (
            <div key={r.rental_id} data-testid={`legal-health-row-${r.rental_id}`}
              className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${HEALTH_META[r.status].color}`}>{HEALTH_META[r.status].label}</span>
                    <span className="font-bold text-sm">{r.unit_label}</span>
                    {r.in_philly && <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">PHILLY</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{r.address}</div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    <ChecklistTile ok={r.license_ok && !r.license_expired} label="Rental License" val={r.license_number} sub={r.license_expired ? "EXPIRED" : (r.license_expires_on ? `expires ${r.license_expires_on}` : "no expiry set")}/>
                    <ChecklistTile ok={r.crs_ok} label="Cert of Rental Suit." val={r.crs_number}/>
                    <ChecklistTile ok={r.lead_cert_ok} label={r.needs_lead_cert ? "Lead-Safe Cert" : "Lead Cert (n/a)"} val={r.needs_lead_cert ? undefined : "post-1978"}/>
                  </div>
                </div>
                <Link to={`/admin/landlord/rentals/${r.rental_id}/compliance`}
                  data-testid={`legal-health-fix-${r.rental_id}`}
                  className="h-9 px-3 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow flex-shrink-0">
                  <UploadSimple weight="fill" size={12}/> Upload Docs
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
function ChecklistTile({ ok, label, val, sub }) {
  return (
    <div className={`p-2 rounded-lg border ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
      <div className="text-[9px] uppercase font-black tracking-widest text-slate-500 flex items-center gap-1">
        {ok ? <CheckCircle weight="fill" size={10} className="text-emerald-500"/> : <Warning weight="fill" size={10} className="text-amber-500"/>}
        {label}
      </div>
      <div className="font-mono text-sm font-bold text-slate-800 mt-0.5 truncate">{val || "—"}</div>
      {sub && <div className="text-[9px] text-slate-500">{sub}</div>}
    </div>
  );
}

// ─── Per-rental compliance uploads page ────────────────────────
const KIND_LABELS = { rental_license: "Rental License", crs: "Cert of Rental Suitability", lead_cert: "Lead-Safe Certificate", other: "Other" };
export function RentalComplianceUpload() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/landlord-auto/rentals/${id}/compliance`);
      setData(r.data); setMeta(r.data.compliance || {});
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, [id]);
  if (!data) return <AdminShell><div className="p-6 text-slate-500">Loading…</div></AdminShell>;

  const saveMeta = async () => {
    setSaving(true);
    try {
      await api.post(`/landlord-auto/rentals/${id}/compliance`, {
        rental_license_number: meta.rental_license_number || null,
        rental_license_expires_on: meta.rental_license_expires_on || null,
        crs_number: meta.crs_number || null,
        crs_issued_on: meta.crs_issued_on || null,
        unit_year_built: meta.unit_year_built ? parseInt(meta.unit_year_built, 10) : null,
        notes: meta.notes || null,
      });
      toast.success("Compliance saved — lease PDF now cites real numbers");
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      await api.post(`/landlord-auto/rentals/${id}/compliance/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${KIND_LABELS[kind]} uploaded`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setUploading(false); }
  };
  const dropFile = async (fid) => {
    if (!window.confirm("Delete this compliance file?")) return;
    try { await api.delete(`/landlord-auto/rentals/${id}/compliance/files/${fid}`); load(); } catch { toast.error("Delete failed"); }
  };

  // Philly Open Data one-tap lookup
  const phillyLookup = async () => {
    setLookingUp(true); setLookupResult(null);
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/compliance/philly-lookup`);
      setLookupResult(r.data);
      if (r.data.auto_filled) {
        toast.success(`License ${r.data.auto_filled_number} auto-filled from Philly L&I`);
        load();
      } else if ((r.data.candidates || []).length === 0) {
        toast.info("No rental licenses found at this address in the Philly Open Data. If the license is brand new it may not be indexed yet.");
      } else {
        toast.info(`${r.data.candidates.length} match${r.data.candidates.length === 1 ? "" : "es"} found — pick one below`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Philly lookup failed")); }
    finally { setLookingUp(false); }
  };
  const pickLicense = async (cand) => {
    try {
      await api.post(`/landlord-auto/rentals/${id}/compliance/philly-lookup/pick`, {
        license_number: cand.license_number,
        expires_on: cand.expires,
      });
      toast.success(`License ${cand.license_number} saved`);
      setLookupResult(null);
      load();
    } catch { toast.error("Pick failed"); }
  };

  const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium";
  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-3xl mx-auto" data-testid="rental-compliance-page">
        <Link to="/admin/landlord/legal-health" className="text-xs text-sky-600 font-black uppercase tracking-widest inline-flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Legal Health</Link>
        <h1 className="font-display font-black text-3xl leading-none mb-1">Compliance Docs</h1>
        <div className="text-xs text-slate-500 mb-4">Real numbers replace the attestation language in the executed lease PDF's Philly Compliance Packet.</div>

        <div className="bg-white border-2 border-fuchsia-500 rounded-2xl p-4 md:p-5 shadow-lg mb-5" data-testid="philly-lookup-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldCheck weight="fill" size={18} className="text-fuchsia-600"/>
              <div>
                <div className="font-black uppercase tracking-widest text-[10px] text-fuchsia-700">Philly L&I one-tap lookup</div>
                <div className="text-[11px] text-slate-500">Pulls your rental license # + expiry from Philadelphia's Open Data (phl.carto.com)</div>
              </div>
            </div>
            <button onClick={phillyLookup} disabled={lookingUp} data-testid="philly-lookup-btn"
              className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl disabled:opacity-50 shadow inline-flex items-center gap-1">
              {lookingUp ? "Looking up…" : "Auto-verify from City"}
            </button>
          </div>
          {lookupResult && (lookupResult.candidates || []).length > 0 && !lookupResult.auto_filled && (
            <div className="mt-3 space-y-1.5" data-testid="philly-lookup-candidates">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">
                {lookupResult.candidates.length} match{lookupResult.candidates.length === 1 ? "" : "es"} for {lookupResult.queried_street} — tap to save
              </div>
              {lookupResult.candidates.map((c, i) => (
                <button key={c.license_number || i} onClick={() => pickLicense(c)}
                  data-testid={`philly-lookup-pick-${c.license_number}`}
                  className={`w-full text-left p-3 border-2 rounded-lg transition ${c.active ? "border-emerald-400 bg-emerald-50 hover:border-emerald-600" : "border-slate-200 bg-slate-50 hover:border-fuchsia-400"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-black text-sm">#{c.license_number}</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${c.active ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"}`}>{c.status || "unknown"}</span>
                    {c.units && <span className="text-[10px] text-slate-600 bg-white border border-slate-200 rounded px-1.5 py-0.5">{c.units} units</span>}
                  </div>
                  <div className="text-xs text-slate-700 mt-0.5">{c.owner_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Issued {c.issued} · Est. expires {c.expires || "unknown"}</div>
                </button>
              ))}
              <div className="text-[10px] text-slate-400 italic pt-1">
                Estimated expiry = 1 year after most-recent-issue date (Philly renews annually). Adjust in the form below if you know a different date.
              </div>
            </div>
          )}
          {/* CRS deep-link: once a license is on file, CRS must be requested
              separately via eclipse.phila.gov within 60 days of lease start.
              Not in Open Data — so we deep-link + remind. */}
          {(meta.rental_license_number || lookupResult?.auto_filled) && (
            <div className="mt-3 p-3 rounded-lg bg-emerald-50 border-l-4 border-emerald-500 flex items-start gap-2" data-testid="crs-reminder-block">
              <ShieldCheck weight="fill" size={16} className="text-emerald-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-black tracking-widest text-emerald-800">CRS reminder</div>
                <div className="text-xs text-slate-700 mt-0.5">
                  License looks valid. Philly also requires a <b>Certificate of Rental Suitability</b> issued
                  within <b>60 days of the lease start date</b>. Request it directly at eclipse.phila.gov
                  (Field Radar can't auto-fetch — CRS isn't in Open Data).
                </div>
                <a href="https://eclipse.phila.gov/phillylmsprod/pub/lms/Login.aspx" target="_blank" rel="noreferrer"
                  data-testid="crs-eclipse-link"
                  className="mt-2 inline-flex items-center gap-1 h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg">
                  Request CRS on eclipse.phila.gov →
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm space-y-3" data-testid="rental-compliance-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Rental License #</div><input value={meta.rental_license_number || ""} onChange={e => setMeta({...meta, rental_license_number: e.target.value})} placeholder="123456-25" data-testid="comp-license-num" className={inputCls}/></label>
            <label><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">License expires on</div><input type="date" value={meta.rental_license_expires_on || ""} onChange={e => setMeta({...meta, rental_license_expires_on: e.target.value})} data-testid="comp-license-exp" className={inputCls}/></label>
            <label><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">CRS #</div><input value={meta.crs_number || ""} onChange={e => setMeta({...meta, crs_number: e.target.value})} placeholder="CRS-2026-000123" data-testid="comp-crs-num" className={inputCls}/></label>
            <label><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">CRS issued on</div><input type="date" value={meta.crs_issued_on || ""} onChange={e => setMeta({...meta, crs_issued_on: e.target.value})} data-testid="comp-crs-issued" className={inputCls}/></label>
            <label><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Unit built (year)</div><input type="number" min="1600" max="2100" value={meta.unit_year_built || ""} onChange={e => setMeta({...meta, unit_year_built: e.target.value})} placeholder="1925" data-testid="comp-year-built" className={inputCls}/></label>
          </div>
          <button onClick={saveMeta} disabled={saving} data-testid="comp-save-btn"
            className="h-11 px-5 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl disabled:opacity-50 shadow">
            {saving ? "Saving…" : "Save Numbers"}
          </button>
        </div>

        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm" data-testid="rental-compliance-uploads">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-3">Attach PDFs / photos</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {["rental_license", "crs", "lead_cert"].map(kind => (
              <label key={kind} className="p-3 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-sky-500 transition text-center" data-testid={`comp-upload-${kind}`}>
                <UploadSimple weight="fill" size={22} className="mx-auto text-slate-400"/>
                <div className="text-xs font-bold text-slate-700 mt-1">{KIND_LABELS[kind]}</div>
                <div className="text-[10px] text-slate-500">PDF or photo · max 10MB</div>
                <input type="file" accept="application/pdf,image/*" className="hidden"
                  onChange={e => upload(kind, e.target.files?.[0])}/>
              </label>
            ))}
          </div>
          {uploading && <div className="text-xs text-slate-500 mt-3 italic">Uploading…</div>}
          {(data.files || []).length > 0 && (
            <div className="mt-4 space-y-1.5">
              {data.files.map(f => (
                <div key={f.id} data-testid={`comp-file-${f.id}`}
                  className="p-2 border border-slate-200 rounded-lg flex items-center gap-2 text-sm">
                  <FileText weight="fill" size={14} className="text-slate-500"/>
                  <span className="font-bold flex-shrink-0">{KIND_LABELS[f.kind] || f.kind}</span>
                  <span className="text-slate-500 truncate flex-1">{f.filename}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{Math.round((f.size_bytes || 0)/1024)} KB</span>
                  <button onClick={() => dropFile(f.id)} className="text-slate-400 hover:text-red-500" aria-label={`Delete ${f.filename}`}>
                    <Trash size={12}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
